import { useEntitlements } from "@frontegg/react";
import PageStructure, { PageContent, Pane, PaneContent } from "@pages/PageStructure.component";
import ApiService, { JobEnqueueResponse, ListRecordsResponse } from "@services/api/api.service";
import { useFeatureFlagValue } from "@services/featureFlags/featureFlag.service";
import { useCallback, useState } from "react";
import { useQuery } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import { Badge, Button, Form, Modal, ProgressBar } from "react-bootstrap";
import AsyncButton from "@components/button/AsyncButton.component";
import BackgroundService from "@services/bg.service";
import { useInstallationInfo } from "@stores/data.store";

export interface Template {
    id: string;
    name: string;
    type: string;
    included_nodes: {
        pipeline_node_id: string;
        included_field_ids: string[];
    }[];
    include_relationship_ids: string[];
    version: number;
    data_model_template_id?: string;
    taxonomic_id?: string;
}

interface TemplatePublishToAllModalProps {
    onConfirm?: (publishToProduction: boolean, performProductionBuild: boolean) => void;
    onClose?: () => void;
    show: boolean;
    templateToPublish: Template | null;
    confirmationButtonVariant?: string;
    confirmationButtonText?: string;
    confirmIcon?: string;
}

function TemplatePublishToAllModal({
    show,
    onClose,
    templateToPublish,
    confirmationButtonText = 'Publish',
    confirmationButtonVariant = 'success',
    confirmIcon = 'mdi-check'
}: TemplatePublishToAllModalProps) {
    const [publishToProduction, setPublishToProduction] = useState(false);
    const [performProductionBuild, setPerformProductionBuild] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [publishStatus, setPublishStatus] = useState<'IDLE' | 'IN_PROGRESS'| 'COMPLETE' | 'ERROR'>('IDLE');
    const [progress, setProgress] = useState(0);

    const { data: installationPlan } = useInstallationInfo(templateToPublish?.id as string);

    const header = `Upgrade ALL ${templateToPublish?.name} (ver ${templateToPublish?.version})`;
    const close = () => {
        // Reset checkbox states when closing
        setPublishToProduction(false);
        setPerformProductionBuild(false);
        setPublishStatus("IDLE");
        setPublishing(false)
        setProgress(0);
        if (onClose) {
            onClose();
        }
    };

    const runPublish = useCallback(async () => {
        if (templateToPublish && publishStatus === 'IDLE') {
            setPublishing(true);
            setPublishStatus('IN_PROGRESS');
            const je = await ApiService.getInstance().request('POST', `/templates/upgrade-all-installations/${templateToPublish.id}`, {
                publish_to_prod: publishToProduction,
                performProductionBuild
                
            }) as JobEnqueueResponse;
            const job_result = await BackgroundService.getInstance().waitForJob(je.job_id, false, (job) => {
                setProgress(job.progress);
            });
            setPublishStatus('COMPLETE');
        }
    }, [templateToPublish, publishToProduction, performProductionBuild, publishStatus]);

    let modalBody;

    if (!publishing) {
        modalBody = (
            <>
                <p className="text-center">
                    Are you sure you want to upgrade
                    <br />  
                    <strong>{installationPlan?.installation_info.total_installations}</strong> accounts that use <strong>{templateToPublish?.name}</strong> to version <strong>{templateToPublish?.version}</strong>?
                </p>
                <hr />
                <p className="text-center">
                    {installationPlan && installationPlan.installation_info.count_at_next > 0 && (
                        <span>
                            <Badge bg="warning" className="ms-2">{installationPlan.installation_info.count_at_next}</Badge> to reinstall
                        </span>
                    )}
                    {installationPlan && installationPlan.installation_info.count_below_next > 0 && (
                        <span>
                            <Badge bg="success" className="ms-2">{installationPlan.installation_info.count_below_next}</Badge> to upgrade
                        </span>
                    )}
                    {installationPlan && installationPlan.installation_info.count_no_version > 0 && (
                        <span>
                            <Badge bg="pliable" className="ms-2">{installationPlan.installation_info.count_no_version}</Badge> to install
                        </span>
                    )}
                </p>
                <hr />
                <Form>
                    <div className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="publish-to-dev"
                            label="Upgrade template in Develop Mode"
                            checked={true}
                            disabled={true}
                        />
                        <Form.Text className="text-muted ms-4">
                            Default behavior; must be checked.
                        </Form.Text>
                    </div>
                    <div className="mb-3">
                    <Form.Check
                        type="checkbox"
                        id="publish-to-production"
                        label="and publish to Production"
                        checked={publishToProduction}
                        onChange={(e) => setPublishToProduction(e.target.checked)}
                    />
                        <Form.Text className="text-muted ms-4">
                            This will publish any changes that are in the account's Develop Mode to Production.
                        </Form.Text>
                    </div>
                    {/* <div className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="perform-production-build"
                            label="and perform Production Build"
                            checked={performProductionBuild}
                            disabled={true}
                            onChange={(e) => setPerformProductionBuild(e.target.checked)}
                        />
                        <Form.Text className="text-muted ms-4">
                            Disabled for account.
                        </Form.Text>
                    </div> */}
                </Form>
            </>
        );
    }else{
        let progressBarColor: string = 'primary';
        if (publishStatus === 'COMPLETE') {
            progressBarColor = 'success';
            confirmationButtonText = 'Published';
        } else if (publishStatus === 'ERROR') {
            progressBarColor = 'error';
        }
        modalBody = (
            <>
                <p>Publishing {templateToPublish?.name}...</p>
                <ProgressBar variant={progressBarColor} now={progress} />
            </>
        );
    }

    return (
        <Modal show={show} onHide={close}>
            <Modal.Header closeButton>
                <Modal.Title>{header}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {modalBody}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={close}>
                    Close
                </Button>
                <AsyncButton
                    icon={`mdi ${confirmIcon}`}
                    variant={confirmationButtonVariant}
                    onClick={runPublish}
                    text={confirmationButtonText}
                    loading={publishing && publishStatus === 'IN_PROGRESS'}
                />
            </Modal.Footer>
        </Modal>
    );
}


export const useMyTemplates = () => {
    return useQuery('my_templates', async () => {
        const response = await ApiService.getInstance().request('GET', '/templates/mine') as ListRecordsResponse<Template>;
        return response.records;
    }, {
        // 30 mins
        staleTime: 60 * 30 * 1000
    });
}

const MyTemplatesPage = () => {
    const navigate = useNavigate();
    const templates = useMyTemplates();

    const isTemplateManagementEnabled = useFeatureFlagValue('template-management', false);

    const [templateIdToPublish, setTemplateIdToPublish] = useState<Template | null>(null);

    const publishToAll = useCallback(async (templateId: Template) => {
        // Logic to update all templates
        setTemplateIdToPublish(templateId);
    }, []);

    if (!isTemplateManagementEnabled) {
        return <>Access denied</>
    }

    return <PageStructure>
        <PageContent>
            
            <Pane>
                <PaneContent>
                    {templateIdToPublish && <TemplatePublishToAllModal
                        templateToPublish={templateIdToPublish}
                        show={templateIdToPublish !== null}
                        onClose={() => setTemplateIdToPublish(null)}
                    />}
            <div className="p-5">
                <div className="d-flex mb-1">
                    <h1 className="mb-0 flex-1">Your Templates</h1>
                    <Link className="btn btn-primary" to="/templates/export">New Template</Link>
                </div>
                <p>View templates managed by this account.</p>
                <table className="table table-bordered">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Type</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {templates.data && templates.data.map(t => {
                            return <tr>
                                <td>
                                    {t.name}
                                </td>
                                <td>
                                    {t.type}
                                </td>
                                <td>
                                    <button className="btn btn-light me-2" onClick={() => { navigate(`/templates/edit/${t.id}`) }}>Edit <i className="mdi mdi-pencil"></i></button>
                                    
                                    <button className="btn btn-light" onClick={() => publishToAll(t)}>Upgrade All <i className="mdi mdi-directions-fork"></i></button>
                                </td>
                            </tr>
                        })}
                    </tbody>
                </table>
            </div>
            </PaneContent>
            </Pane>
        </PageContent>
    </PageStructure>
}

export default MyTemplatesPage;